import { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@/utils/tailwind';

type RotatingPlaceholderInputProps = {
  placeholders: string[];
  intervalMs?: number;
  placeholderClassName?: string;
} & Omit<React.ComponentProps<'input'>, 'placeholder'>;

export const RotatingPlaceholderInput: React.FC<
  RotatingPlaceholderInputProps
> = ({
  placeholders,
  intervalMs = 5000,
  placeholderClassName,
  className,
  value: controlledValue,
  ...inputProps
}) => {
  const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
  const [internalValue, setInternalValue] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const safeIndex =
    placeholders.length > 0 ? currentPlaceholder % placeholders.length : 0;
  const displayValue = controlledValue ?? internalValue;
  const showPlaceholder = !displayValue;

  useEffect(() => {
    if (placeholders.length <= 1) return;

    const start = () => {
      intervalRef.current = setInterval(() => {
        setCurrentPlaceholder(prev => (prev + 1) % placeholders.length);
      }, intervalMs);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        start();
      }
    };

    start();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [placeholders, intervalMs]);

  return (
    <div className="relative w-full">
      <input
        {...inputProps}
        value={displayValue}
        onChange={e => {
          if (controlledValue === undefined) setInternalValue(e.target.value);
          inputProps.onChange?.(e);
        }}
        className={cn('relative z-10 w-full bg-transparent', className)}
      />

      <div className="pointer-events-none absolute inset-0 flex items-center">
        <AnimatePresence mode="wait">
          {showPlaceholder && placeholders.length > 0 && (
            <motion.span
              key={`placeholder-${safeIndex}`}
              initial={{ y: 5, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -15, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'linear' }}
              className={cn(
                'w-full truncate text-neutral-500',
                placeholderClassName,
              )}
            >
              {placeholders[safeIndex]}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
